import { useState, useRef, useCallback } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/utils'

interface SearchBarProps {
  onSearch:   (query: string) => void
  placeholder?: string
  defaultValue?: string
  autoFocus?: boolean
  className?: string
}

export function SearchBar({
  onSearch,
  placeholder = 'Search photos, tags, people…',
  defaultValue = '',
  autoFocus = false,
  className,
}: SearchBarProps) {
  const [value, setValue] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = value.trim()
      if (trimmed) onSearch(trimmed)
    },
    [value, onSearch],
  )

  const handleClear = () => {
    setValue('')
    inputRef.current?.focus()
  }

  return (
    <form
      role="search"
      onSubmit={handleSubmit}
      className={cn('relative flex w-full items-center', className)}
    >
      <label htmlFor="global-search" className="sr-only">
        Search photos
      </label>
      <Search
        className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        ref={inputRef}
        id="global-search"
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        className={cn(
          'h-9 w-full rounded-md border bg-background pl-9 pr-8',
          'text-sm placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'transition-colors',
        )}
        aria-label="Search"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </form>
  )
}
